import React, { useEffect, useRef } from 'react';
import Card from '../Card/Card';
import { useAppState } from '../../context/AppStateProvider/useAppState';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import ListItem from '@mui/material/ListItem';
import { useApi } from '../../context/ApiProvider/useApi';
import { CircularProgress } from '@mui/material';

const Grid: React.FC<{ className?: string }> = ({ className }) => {
    const { state } = useAppState();
    const { filteredEvents, activeEvent, user, viewMode } = state;
    const [activeSection, setActiveSection] = React.useState('all-day');
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { eventLoading, itineraryLoading } = useApi();
    const prevFilteredEventsIds = useRef<any[]>([]);

    // Scroll to active event when it changes
    useEffect(() => {
        if (activeEvent && scrollContainerRef.current) {
            const element = cardRefs.current[activeEvent.id];
            const container = scrollContainerRef.current;

            if (element) {
                const rect = element.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();

                // Check if the element is currently outside the visible viewport of the container
                const isVisible = rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;

                if (!isVisible) {
                    element.scrollIntoView({
                        behavior: 'instant',
                        block: 'center', // This centers the card in the list
                        inline: 'nearest',
                    });

                    // screen will jump so recenter map to top
                    requestAnimationFrame(() => {
                        const host = document.getElementById('open-day-map-root');
                        if (host?.shadowRoot) {
                            const tabs = host.shadowRoot.getElementById('open-day-map_tabs');
                            if (tabs) {
                                tabs.scrollIntoView({ behavior: 'instant', block: 'start' });
                            }
                        }
                    });
                }
            }
        }
    }, [activeEvent]);

    const sections = [
        { title: 'All day', timeslot: 'all-day' },
        { title: '10am', timeslot: '1000' },
        { title: '11am', timeslot: '1100' },
        { title: '12pm', timeslot: '1200' },
        { title: '1pm', timeslot: '1300' },
        { title: '2pm', timeslot: '1400' },
    ];

    // Create refs for each section
    const sectionRefs = useRef<Record<string, HTMLLIElement | null>>({});

    const cardRefs = useRef<Record<string, HTMLDivElement | HTMLLIElement | null>>({});

    const scrollToSection = (timeslot: string) => {
        const section = sectionRefs.current[timeslot];
        const container = scrollContainerRef.current;

        if (section && container) {
            section.scrollIntoView({ behavior: 'instant', block: 'start' });

            // screen will jump so recenter map to top
            requestAnimationFrame(() => {
                const host = document.getElementById('open-day-map-root');
                if (host?.shadowRoot) {
                    const tabs = host.shadowRoot.getElementById('open-day-map_tabs');
                    if (tabs) {
                        tabs.scrollIntoView({ behavior: 'instant', block: 'start' });
                    }
                }
            });

            // --- GTM TRACKING ---
            (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];
            (window as any).dataCoreLayer.push({
                event: 'od_planner_filter_click_time',
                click_text: timeslot,
                click_classes: 'time_slot',
            });
        }
    };

    const handleScroll = () => {
        if (!scrollContainerRef.current) return;

        const containerTop = scrollContainerRef.current.scrollTop;
        // Add a small offset to account for the sticky header or visual breathing room
        const offset = 50;

        // Find the current active section
        let currentActive = sections[0].timeslot;

        for (const section of sections) {
            const el = sectionRefs.current[section.timeslot];
            if (el) {
                // Since 'el' is inside the relative container, el.offsetTop is relative to the top of the list
                // If el.offsetTop <= containerTop + offset, it means we've scrolled past the start of this section
                if (el.offsetTop <= containerTop + offset) {
                    currentActive = section.timeslot;
                }
            }
        }

        // Only update if changed to avoid renders
        if (currentActive !== activeSection) {
            setActiveSection(currentActive);
        }
    };

    useEffect(() => {
        const currentEvents = filteredEvents || [];
        // Map IDs to strings to ensure consistent comparison
        const currentIds = currentEvents.map((e) => String(e.id));
        const prevIds = prevFilteredEventsIds.current;

        const hasChanged = currentIds.length !== prevIds.length || !currentIds.every((id, i) => id === prevIds[i]);

        if (hasChanged) {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0;
                setActiveSection('all-day');
            }
        }

        prevFilteredEventsIds.current = currentIds;
    }, [filteredEvents]);

    return (
        <div className={`flex flex-1 flex-col overflow-hidden bg-[#F4F4F4] p-[4px] px-2 ${className}`}>
            <div className='z-10 w-full pt-3'>
                <div className='flex items-start justify-between gap-3 pb-0 md:px-4'>
                    {/* Navigation Links */}
                    <div className='scrollbar-hide flex w-full overflow-x-auto px-2 pb-4'>
                        {sections.map((section) => (
                            <button
                                key={section.timeslot}
                                onClick={() => scrollToSection(section.timeslot)}
                                className={`mr-6 flex-shrink-0 cursor-pointer text-[17px] font-bold transition-colors duration-200 last:mr-0 md:text-[18px] ${
                                    activeSection === section.timeslot
                                        ? 'border-b-3 border-[var(--colour-gold)] text-(--colour-charcoal-dark)'
                                        : 'border-b-3 border-transparent text-gray-500 hover:text-(--colour-charcoal-dark)'
                                }`}>
                                {section.title}
                            </button>
                        ))}
                    </div>
                    {/* Print Link Component */}
                    {viewMode == 'itinerary' && (filteredEvents || []).length > 0 && (
                        <a
                            href={`${typeof window !== 'undefined' && window.location.hostname === 'www.curtin.edu.au' ? '/open-day' : ''}/itinerary/print/`}
                            target='_blank'
                            onClick={() => {
                                (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];
                                (window as any).dataCoreLayer.push({
                                    event: 'od_planner_button_click_print_plan',
                                    click_text: 'Print day plan',
                                    click_class: 'button-print-plan',
                                });
                            }}
                            className="flex-shrink-0 justify-center pb-4 font-['Effra'] text-base text-[17px] leading-6 font-normal text-[var(--colour-charcoal-dark)] underline md:text-[18px]">
                            Print day plan
                        </a>
                    )}
                </div>
            </div>

            {/* Event List */}
            {(filteredEvents || []).length === 0 ? (
                <div className='m-4 flex h-full items-start rounded-[16px] bg-[var(--colour-white)] p-[16px] outline outline-[0.75px] outline-offset-[-0.75px] outline-[var(--colour-grey-light-50)]'>
                    {viewMode == 'events' && (
                        <>
                            {' '}
                            {eventLoading ? (
                                <div className='flex h-[100%] w-full items-center justify-center'>
                                    <CircularProgress sx={{ color: '#cc9900' }} />
                                </div>
                            ) : (
                                <div className='p-4 text-center text-sm text-gray-500'>
                                    No events found. Try adjusting your filters.
                                </div>
                            )}
                        </>
                    )}

                    {viewMode == 'itinerary' && (
                        <>
                            {' '}
                            {itineraryLoading ? (
                                <div className='flex h-[100%] w-full items-center justify-center'>
                                    <CircularProgress sx={{ color: '#cc9900' }} />
                                </div>
                            ) : (
                                <>
                                    {user ? (
                                        <div className='p-4 text-center text-sm text-gray-500'>
                                            No saved events found.
                                        </div>
                                    ) : (
                                        <> </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            ) : (
                <div
                    ref={scrollContainerRef}
                    className='flex-1 overflow-y-auto'
                    onScroll={handleScroll}>
                    <List
                        sx={{
                            'width': '100%',
                            '& ul': { padding: 0 },
                            'backgroundColor': '#F4F4F4',
                            'scroll-behavior': 'auto !important',
                        }}
                        subheader={<li />}>
                        {sections.map((section) => {
                            const eventsForSection = (filteredEvents || []).filter(
                                (event) => event.timeslot === section.timeslot,
                            );

                            return (
                                <li
                                    key={`section-${section.timeslot}`}
                                    ref={(el) => {
                                        sectionRefs.current[section.timeslot] = el;
                                    }}>
                                    <ul>
                                        <ListSubheader
                                            disableSticky={true}
                                            sx={{
                                                textAlign: 'left',
                                                fontSize: '18px',
                                                color: 'rgba(0, 0, 0, 0.6)',
                                                backgroundColor: '#F4F4F4', // Ensure subheader background matches to avoid transparency issues
                                            }}>
                                            <span className='font-semibold'>{section.title} </span>
                                        </ListSubheader>
                                        {eventsForSection.length === 0 ? (
                                            <ListItem>
                                                <div className='w-full max-w-full rounded-[16px] bg-[var(--colour-white)] p-4 shadow-md'>
                                                    {viewMode == 'itinerary'
                                                        ? 'You have saved no sessions at this time'
                                                        : '👀 Sorry, there are no sessions at this time'}
                                                </div>
                                            </ListItem>
                                        ) : (
                                            eventsForSection.map((event) => (
                                                <ListItem
                                                    key={`item-${section.timeslot}-${event.id}`}
                                                    id={`event-card-${event.id}`}
                                                    className={`${activeEvent?.id === event.id ? 'Mui-selected' : ''} [contain:layout_block-size] [contain-intrinsic-size:auto_186px] [content-visibility:auto]`}
                                                    ref={(el) => {
                                                        cardRefs.current[event.id] = el;
                                                    }}>
                                                    <Card event={event} />
                                                </ListItem>
                                            ))
                                        )}
                                    </ul>
                                </li>
                            );
                        })}
                        <li key={`section-1500`}>
                            <ul>
                                <ListSubheader
                                    sx={{
                                        textAlign: 'left',
                                        textTransform: 'uppercase',
                                        fontWeight: 600,
                                        fontSize: '1.2em',
                                        color: 'rgba(0, 0, 0, 0.6)',
                                        backgroundColor: '#F4F4F4',
                                    }}>
                                    3PM
                                </ListSubheader>
                                <ListItem>
                                    <div className='w-full max-w-full rounded-[16px] bg-[var(--colour-white)] p-4 shadow-md'>
                                        👋 That's it for the day! Thanks for coming along
                                    </div>
                                </ListItem>
                            </ul>
                        </li>
                    </List>
                </div>
            )}
        </div>
    );
};

export default Grid;
